import React from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import moment from 'moment-timezone';
import { BuildingLibraryIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { formatCurrency } from '../../../utils/baseUtils';
import { cn } from '../../../components/ui/_cn';

dayjs.extend(utc);

// Frame 2c's left card: one sentence and a line of steps answering "where
// is my money" (spec 2026-09-22 §6).
//
// Two clocks (owner, 2026-10-22). `arrivalDate` is a DAY Stripe stated, and
// rendering a day through any other clock shifts it across midnight, so it
// reads in UTC. `createdAt`, `statusAt` and `paidAt` are instants: they read
// on the store's wall clock, like the transaction rows under this card
// (`zonedDateTimeCell`), so a payout whose first sale was Oct 20, 5:35 PM
// does not say "Since Oct 21". The store's zone is the first transaction
// row's; a payout with no zone anywhere reads in UTC. The CSV export keeps
// UTC, deliberately.
//
// Stripe's statuses as the owner's "Payout States" mock draws them (frames
// 5a to 5f, 2026-10-06/07). `current` is the step the payout is on;
// `moving` marks the states in which it really is on its way, and only
// those animate. `failed` draws the current step as a red cross, `stopped`
// as a grey one: nothing went wrong with a canceled payout, it was called
// off.
//
// `pending` is a payout Stripe has created and not yet sent: its first step
// is in progress under its own name, and the second is only expected.
// `returned` is not a Stripe status. The bank sent the money back after it
// was deposited, which Stripe reports as `failed`, the same word as a
// payout that never arrived; the API tells them apart (`returned`), and the
// page draws four steps so that the deposit that did happen stays green.
const STATES = {
  paid: { lead: 'Deposited', verb: 'was deposited to', current: 2 },
  in_transit: { lead: 'Arriving', verb: 'is on its way to', current: 1, moving: true },
  pending: { lead: 'Arriving', verb: 'is being prepared for', current: 0, moving: true },
  failed: { lead: 'Payout', tail: 'failed', current: 2, failed: true },
  canceled: { lead: 'Payout canceled', current: 1, stopped: true },
  returned: { lead: 'Returned', tail: 'by your bank', current: 3, failed: true },
  // The Upcoming detail page (owner, 2026-09-28): the money is in the
  // balance and the payout that takes it is still to come — so the first
  // step is the one in progress, moving towards "Sent" (owner, 2026-10-07).
  upcoming: { lead: 'Arriving', verb: 'will be sent to', current: 0, moving: true },
};

export const stateKey = (d) => (d.status === 'failed' && d.returned ? 'returned' : d.status);

// The store's zone for a payout's instants: the first of its charges,
// refunds and adjustments that names one. A zone moment-timezone does not
// know is no zone (UTC), never a thrown error.
export const payoutZone = (detail) => {
  const rows = [...(detail?.charges || []), ...(detail?.refunds || []), ...(detail?.adjustments || [])];
  const zone = rows.map((row) => row?.timezoneId).find((id) => id && moment.tz.zone(id));
  return zone || null;
};

// An instant on the store's wall clock (UTC when there is no zone).
export const atZone = (value, zone, format) => {
  const instant = moment.utc(value);
  return (zone ? instant.tz(zone) : instant).format(format);
};

// A calendar day Stripe stated, in UTC.
const day = (value) => (value ? dayjs.utc(value).format('ddd, MMM D') : null);
// The headline names the weekday in full ("Arriving Wednesday, Sep 23", both
// mocks); the steps under it keep the short form.
const longDay = (value) => (value ? dayjs.utc(value).format('dddd, MMM D') : null);

// Where the dots sit on the one line, by how many there are: the two ends
// and the points between. The labels under them follow: left, centre(s),
// right. A segment is the piece of line between two dots (14px is a dot's
// radius); it is painted green behind every step that is done.
const GEOMETRY = {
  3: {
    dots: ['left-0', 'left-1/2 -ml-3.5', 'right-0'],
    segments: ['left-3.5 w-[calc(50%-14px)]', 'left-1/2 w-[calc(50%-14px)]'],
    labels: ['text-left', 'text-center', 'text-right'],
    grid: 'grid-cols-3',
  },
  4: {
    dots: ['left-0', 'left-[calc(33.333%-14px)]', 'left-[calc(66.667%-14px)]', 'right-0'],
    segments: ['left-3.5 w-[calc(33.333%-14px)]', 'left-[33.333%] w-[33.333%]', 'left-[66.667%] w-[calc(33.333%-14px)]'],
    labels: ['text-left', 'text-center', 'text-center', 'text-right'],
    // Columns of 1:2:2:1, so the two middle labels are centred at a third
    // and two thirds of the width, under their dots; the end labels keep
    // the ends.
    grid: 'grid-cols-[1fr_2fr_2fr_1fr]',
  },
};

const DOT_TONE = {
  done: 'bg-fin-green',
  current: 'bg-fin-blue',
  failed: 'bg-fin-red',
  stopped: 'bg-fin-ink-2',
  future: 'border-2 border-fin-border-2 bg-fin-surface',
};

// A canceled payout's line is neutral from end to end (mock 5e): nothing
// went wrong, nothing is coming. What was done is grey, not green; the
// deposit that never comes is an empty ring behind a dashed line, with no
// bank in it.
const NEUTRAL_DOT_TONE = {
  done: 'bg-fin-muted',
  stopped: 'bg-fin-ink-2',
  future: 'border-2 border-fin-border bg-fin-surface',
};

// The piece of line between two dots takes the colour of the step it leads
// into: green up to the step the payout is on, red into one that failed,
// muted into one that was called off, dashed after it. A step still to
// come leaves the base line showing.
const segmentTone = (into, after, neutral) => {
  if (after === 'stopped') return 'h-0 border-t-[3px] border-dashed border-fin-border';
  // Green up to the step the payout is on (mock 5b: the line behind a
  // moving step is already green); red or muted into the step it ended on.
  if (into === 'done' || into === 'current') return neutral ? 'bg-fin-muted' : 'bg-fin-green';
  if (into === 'failed') return 'bg-fin-red';
  if (into === 'stopped') return 'bg-fin-muted';
  return null;
};

// `bank` draws the destination's icon in a dot that is still to come: the
// last step is the bank. Faint while it is two steps away, ink once it is
// next (mock 5a against 5b).
const DotMark = ({ state, bank, next }) => {
  if (state === 'done') return <CheckIcon className="h-3 w-3 stroke-[3] text-fin-on-green" />;
  if (state === 'current') return <span className="h-2 w-2 rounded-full bg-fin-surface" />;
  if (state === 'failed' || state === 'stopped') return <XMarkIcon className="h-3 w-3 stroke-[3] text-fin-surface" />;
  if (state === 'future' && bank) {
    return <BuildingLibraryIcon className={cn('h-3.5 w-3.5', next ? 'text-fin-ink' : 'text-fin-faintest')} />;
  }
  return null;
};

// The shortfall page's lone dot, in the line of text rather than on a track.
const Dot = ({ state }) => (
  <span className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-full', DOT_TONE[state])} aria-hidden="true">
    <DotMark state={state} />
  </span>
);

const PayoutTimeline = ({ detail }) => {
  const d = detail || {};
  // The payout's own amount, not the sum of the groups — the same figure the
  // header prints and the bank will show.
  const amount = formatCurrency(d.amount || 0);

  // A shortfall (owner, 2026-09-23): an Upcoming group below zero is not
  // waiting for a payout at all. Stripe takes it from the next sales.
  if (d.status === 'upcoming' && (d.amount || 0) < 0) {
    return (
      <div className="px-7 pb-7 pt-[26px]">
        <p className="text-[13px] font-semibold text-fin-muted">Timeline</p>
        <h2 className="mt-1.5 text-[26px] font-bold tracking-[-0.025em] text-fin-ink">Nothing to send</h2>
        <p className="mt-1.5 text-[13.5px] text-fin-muted">
          {`Your balance is ${formatCurrency(Math.abs(d.amount))} below zero, usually because of a refund.`}
        </p>
        <div className="mt-[30px]">
          <div className="flex items-center gap-2">
            <Dot state="current" />
          </div>
          <p className="mt-3 text-[14px] font-bold text-fin-blue-deep">Taken from your next sales</p>
          <p className="mt-1 text-[13px] text-fin-muted">Or from your bank account if there are none.</p>
        </div>
      </div>
    );
  }

  const key = stateKey(d);
  const meta = STATES[key] || STATES.pending;
  const arrival = day(d.arrivalDate);
  // Stripe always names a destination once a payout is sent, but a bankless
  // fixture (or a bank Stripe hasn't reported yet) must not read "…on its
  // way to ." — the sentence falls back to the generic noun instead.
  const bank = d.bankName
    ? [d.bankName, d.bankLast4 ? `•••• ${d.bankLast4}` : null].filter(Boolean).join(' ')
    : 'your bank account';

  // The schedule's delay is how long a standard payout takes; an instant one
  // arrives in minutes, so it names none (reachable since a manual payout's
  // page draws instead of failing — owner, 2026-09-23).
  const delay = d.method !== 'instant' && typeof d.scheduleDelayDays === 'number'
    ? ` · ${d.scheduleDelayDays} business days`
    : '';

  const upcoming = key === 'upcoming';
  const pending = key === 'pending';
  const paid = key === 'paid';
  const zone = payoutZone(d);
  // Instants, on the store's clock.
  const instantDay = (value) => (value ? atZone(value, zone, 'ddd, MMM D') : null);
  const sentOn = d.createdAt ? `Sent ${atZone(d.createdAt, zone, 'MMM D')}` : '—';
  const initiated = {
    // In progress while Stripe has not sent it; a fact once it has.
    title: pending ? 'Processing' : 'Initiated',
    sub: d.createdAt ? atZone(d.createdAt, zone, 'MMM D, h:mm A') : '—',
  };
  // The day a payout failed, was canceled or was returned is the day its
  // status changed (API `statusat`); the day it was deposited is the day
  // Stripe first called it paid, or failing that the arrival day it stated.
  const changed = instantDay(d.statusAt) || '—';
  const deposited = instantDay(d.paidAt) || arrival || '—';

  // An Upcoming payout has no send time yet. Its steps are named as 5a
  // names a pending payout's (owner, 2026-10-07: one vocabulary for money
  // that has not arrived), and say where the money is: processing since
  // the first sale still in the balance, not yet in transit, and the day
  // Stripe's schedule gives for its arrival. No send date is guessed —
  // Stripe names none before it sends.
  const steps = upcoming ? [
    {
      title: 'Processing',
      sub: d.createdAt ? `Since ${atZone(d.createdAt, zone, 'MMM D')}` : '—',
    },
    { title: 'In transit', sub: 'Not yet' },
    { title: 'Deposited', sub: arrival ? `Expected ${arrival}` : '—' },
  ] : key === 'failed' ? [
    initiated,
    { title: 'In transit', sub: sentOn },
    // Never "Deposited" for money that never landed (mock 5d's DEV NOTE).
    { title: 'Failed', sub: changed },
  ] : key === 'canceled' ? [
    initiated,
    // Only a pending payout can be canceled, so it never went in transit.
    { title: 'Canceled', sub: d.statusAt ? atZone(d.statusAt, zone, 'MMM D, h:mm A') : '—' },
    { title: 'Deposited', sub: '—' },
  ] : key === 'returned' ? [
    initiated,
    { title: 'In transit', sub: sentOn },
    { title: 'Deposited', sub: deposited },
    { title: 'Returned', sub: changed },
  ] : [
    initiated,
    {
      title: 'In transit',
      sub: !d.createdAt
        ? '—'
        : pending
          ? `Expected ${atZone(d.createdAt, zone, 'MMM D')}`
          // How long it takes is a promise for a payout still on its way;
          // one that has landed just says when it was sent.
          : `${sentOn}${paid ? '' : delay}`,
    },
    {
      title: 'Deposited',
      sub: arrival ? (paid ? arrival : `Expected ${arrival}`) : '—',
    },
  ];
  const geometry = GEOMETRY[steps.length] || GEOMETRY[3];
  const last = steps.length - 1;

  const stateOf = (index) => {
    if (index < meta.current) return 'done';
    if (index === meta.current) {
      if (meta.failed) return 'failed';
      if (meta.stopped) return 'stopped';
      return paid ? 'done' : 'current';
    }
    return 'future';
  };

  // Only a payout that is on its way moves: the dot it is on pulses, and a
  // light travels along the piece of line it has still to cover.
  const moving = !!meta.moving;
  // The step whose label is green: the deposit, once the money has landed —
  // also on a returned payout, where the deposit did happen before the bank
  // sent it back.
  const landed = paid ? last : key === 'returned' ? 2 : -1;

  return (
    <div className="px-7 pb-7 pt-[26px]">
      <p className="text-[13px] font-semibold text-fin-muted">Timeline</p>
      <h2 className="mt-1.5 text-[26px] font-bold tracking-[-0.025em] text-fin-ink">
        {upcoming && !arrival ? 'Not sent yet' : meta.lead}
        {meta.tail ? (
          <>
            {' '}
            <span className="text-fin-red">{meta.tail}</span>
          </>
        ) : arrival && !meta.failed && !meta.stopped ? (
          <>
            {' '}
            <span className={paid ? 'text-fin-green' : 'text-fin-blue-deep'}>{longDay(d.arrivalDate)}</span>
          </>
        ) : null}
      </h2>
      <p className="mt-1.5 text-[13.5px] text-fin-muted">
        {key === 'failed'
          ? `${amount} could not be deposited to ${bank}. The funds were returned to your balance.`
          : key === 'canceled'
            ? `This payout was canceled before it was sent. ${amount} remains in your balance.`
            : key === 'returned'
              ? `${d.bankName ? bank : 'Your bank'} sent ${amount} back after it was deposited. The funds were returned to your balance.`
              : `${amount} ${meta.verb} ${bank}.`}
      </p>

      {/* One line with a dot at each end and one (or two) between (mock 5a–5f).
          Decorative: the labels under it carry the same facts in words. */}
      <div className="relative mt-[30px] flex h-7 items-center" aria-hidden="true">
        <span className="absolute left-3.5 right-3.5 h-[3px] rounded-sm bg-fin-border" />
        {geometry.segments.map((at, index) => (
          <span
            key={at}
            data-testid={`timeline-segment-${index}`}
            className={cn(
              'absolute h-[3px] overflow-hidden rounded-sm',
              at,
              segmentTone(stateOf(index + 1), stateOf(index), !!meta.stopped),
            )}
          >
            {moving && index === meta.current ? (
              <span
                data-testid="timeline-traveler"
                data-segment={index}
                className="absolute inset-y-0 -ml-5 w-10 animate-fin-tl-travel bg-[linear-gradient(90deg,rgb(var(--fin-blue)/0)_0%,rgb(var(--fin-blue))_50%,rgb(var(--fin-blue)/0)_100%)] motion-reduce:hidden"
              />
            ) : null}
          </span>
        ))}
        {steps.map((step, index) => {
          const state = stateOf(index);
          return (
            <span
              key={step.title}
              data-testid={`timeline-dot-${index}`}
              data-state={state}
              className={cn(
                'absolute flex h-7 w-7 items-center justify-center rounded-full',
                geometry.dots[index],
                (meta.stopped && NEUTRAL_DOT_TONE[state]) || DOT_TONE[state],
                moving && state === 'current' && 'animate-fin-tl-pulse motion-reduce:animate-none',
              )}
            >
              <DotMark
                state={state}
                bank={index === last && !meta.stopped}
                // "Next" only while the payout is still moving towards it: a
                // canceled payout's deposit is not next, it never comes.
                next={!meta.failed && !meta.stopped && index === meta.current + 1}
              />
            </span>
          );
        })}
      </div>

      <div className={cn('mt-4 grid gap-2', geometry.grid)}>
        {steps.map((step, index) => {
          const state = stateOf(index);
          return (
            <div key={step.title} className={cn('min-w-0', geometry.labels[index])}>
              <p
                className={cn(
                  'text-[14px] font-bold',
                  state === 'current' && 'text-fin-blue-deep',
                  state === 'failed' && 'text-fin-red',
                  state === 'stopped' && 'text-fin-ink-2',
                  state === 'future' && 'text-fin-faintest',
                  state === 'done' && (index === landed ? 'text-fin-green' : 'text-fin-ink'),
                )}
              >
                {step.title}
              </p>
              <p className="mt-[3px] text-[13px] tabular-nums text-fin-muted">{step.sub}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PayoutTimeline;
